import { Button, Input } from 'components/atoms';
import { DescriptiveInputWrapper, FormInput } from 'components/molecules';
import PopupForm from 'components/molecules/popup/form/PopupForm';
import { passwordRegExp } from 'constants/passwordRegExp';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import i18next, { t } from 'i18next';
import { useStoreState } from 'providers/StoreStateContext';
import { PiFingerprintSimple } from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useChangePassword } from 'services/auth/changePassword';
import { IChangePasswordForm } from 'services/auth/types';
import { THEME_TYPE } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { object, ref, string } from 'yup';

const newPasswordInitialValues: IChangePasswordForm = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const newPasswordValidationSchema = object().shape({
  currentPassword: string().required(i18next.t('formik-error.required')),
  newPassword: string()
    .matches(passwordRegExp, i18next.t('formik-error.invalid-password'))
    .required(i18next.t('formik-error.required')),
  confirmPassword: string()
    .oneOf([ref('newPassword'), ''], i18next.t('formik-error.unmatch-password'))
    .required(i18next.t('formik-error.required')),
});

const UserSettings = () => {
  const changePassword = useChangePassword();

  const { setThemeType, themeType, theme } = useStoreState();

  const mutateOprions = useMutateOptions<IChangePasswordForm>();
  const { getSelectOptions } = useEnumToSelectOptions();

  const onChangePassword = async (
    values: IChangePasswordForm,
    formikHelpers: FormikHelpers<IChangePasswordForm>,
    actions: PopupActions | null
  ) =>
    await changePassword.mutateAsync(values, {
      ...mutateOprions(formikHelpers),
      onSuccess: () => {
        actions?.close();
        formikHelpers.resetForm();
      },
    });
  // localStorage.setItem('theme-mode', '2');
  // setThemeType(THEME_TYPE.dark);
  return (
    <div className='flex flex-col gap-[30px] w-[370px]'>
      {!!theme?.canChangeThemeMode && (
        <DescriptiveInputWrapper
          header={t('setting.user.theme')}
          description={t('setting.user.themeDescription')}>
          <Input.Select
            label={t('input.label.defaultThemeMode')}
            options={getSelectOptions(THEME_TYPE, 'enum.THEME_TYPE')}
            isClearable={false}
            value={{
              label: t(`enum.THEME_TYPE.${THEME_TYPE[themeType ?? 1]}`),
              value: themeType,
            }}
            onChange={o => {
              localStorage.setItem('theme-mode', `${o}`);
              setThemeType(o.value as THEME_TYPE);
            }}
          />
        </DescriptiveInputWrapper>
      )}
      <DescriptiveInputWrapper
        header={t('setting.user.security')}
        description={t('setting.user.securityDescription')}>
        <PopupForm
          initialValues={newPasswordInitialValues}
          onSubmit={onChangePassword}
          validationSchema={newPasswordValidationSchema}
          submitButton={
            <Button type='submit' variant={BUTTON_VARIANT.submit}>
              <Button.Text text={t('button.change')} />
            </Button>
          }
          header={
            <>
              <PiFingerprintSimple /> {t('reset-password.button')}
            </>
          }
          trigger={
            <Button>
              <Button.Icon icon={<PiFingerprintSimple />} />
              <Button.Text text={t('reset-password.button')} />
            </Button>
          }>
          <FormInput.Text
            name='currentPassword'
            type='password'
            label={t('input.label.current-password')}
          />
          <div />
          <FormInput.Text
            name='newPassword'
            type='password'
            label={t('input.label.new-password')}
          />
          <FormInput.Text
            name='confirmPassword'
            type='password'
            label={t('input.label.confirm-password')}
          />
        </PopupForm>
      </DescriptiveInputWrapper>
    </div>
  );
};

export default UserSettings;
